import AddIcon from '@mui/icons-material/Add'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { describeError } from '../../api/client'
import { useContractors, useSuppliers, type SupplyScope } from '../../api/execution'
import { useLabourCategories, useMaterials } from '../../api/hooks'
import {
  useCreateTemplate,
  useSiteWorkItems,
  useUpdateTemplate,
  type EntryTemplate,
  type TemplateLineRequest,
  type TemplateModule,
} from '../../api/templates'

/** One editable line. Everything is a string while being typed, as forms are. */
interface DraftLine {
  key: number
  workItemId: string
  labourCategoryId: string
  contractorId: string
  materialId: string
  supplierId: string
  supplyScope: SupplyScope
  workerCount: string
  workingHours: string
  overtimeHours: string
  defaultQuantity: string
  rate: string
}

let nextKey = 1
const emptyLine = (): DraftLine => ({
  key: nextKey++,
  workItemId: '',
  labourCategoryId: '',
  contractorId: '',
  materialId: '',
  supplierId: '',
  supplyScope: 'OWN',
  workerCount: '',
  workingHours: '',
  overtimeHours: '',
  defaultQuantity: '',
  rate: '',
})

const GRID: Record<TemplateModule, string> = {
  WORK: '3fr 1.2fr 40px',
  LABOUR: '2fr 2fr 1fr 1fr 1fr 40px',
  MATERIAL: '2.4fr 1.8fr 1.4fr 1.2fr 1fr 40px',
}

const HEADERS: Record<TemplateModule, string[]> = {
  WORK: ['BOQ line', 'Planned quantity', ''],
  LABOUR: ['Labours', 'Whose labour', 'Planned workers', 'Hours', 'OT', ''],
  MATERIAL: ['Material', 'Supplier', 'Whose material', 'Ordered quantity', 'Rate', ''],
}

/**
 * Builds a template line by line, or edits one that already exists.
 *
 * <p>The quantities here are a starting point for a day, not a record of one: applying the
 * template writes them into a fresh entry that the engineer then corrects against what
 * actually happened.</p>
 */
export function TemplateEditorDialog({
  siteId,
  module,
  template,
  onClose,
}: {
  siteId: string
  module: TemplateModule
  /** Absent when creating. */
  template?: EntryTemplate
  onClose: () => void
}) {
  const workItems = useSiteWorkItems(module === 'WORK' ? siteId : undefined)
  const categories = useLabourCategories()
  const contractors = useContractors()
  const materials = useMaterials({ page: 0, size: 300 })
  const suppliers = useSuppliers()

  const create = useCreateTemplate()
  const update = useUpdateTemplate()

  const [name, setName] = useState(template?.name ?? '')
  const [description, setDescription] = useState(template?.description ?? '')
  const [active, setActive] = useState(template?.active ?? true)
  const [lines, setLines] = useState<DraftLine[]>(
    template
      ? template.lines.map((line) => ({
          key: nextKey++,
          workItemId: line.workItemId ?? '',
          labourCategoryId: line.labourCategoryId ?? '',
          contractorId: line.contractorId ?? '',
          materialId: line.materialId ?? '',
          supplierId: line.supplierId ?? '',
          supplyScope: line.supplyScope ?? 'OWN',
          workerCount: line.workerCount != null ? String(line.workerCount) : '',
          workingHours: line.workingHours != null ? String(line.workingHours) : '',
          overtimeHours: line.overtimeHours != null ? String(line.overtimeHours) : '',
          defaultQuantity: line.defaultQuantity != null ? String(line.defaultQuantity) : '',
          rate: line.rate != null ? String(line.rate) : '',
        }))
      : [emptyLine(), emptyLine(), emptyLine()],
  )
  const [error, setError] = useState<unknown>(null)

  function set(key: number, field: keyof DraftLine, value: string) {
    setLines((prev) =>
      prev.map((line) => {
        if (line.key !== key) return line
        const next = { ...line, [field]: value } as DraftLine
        // Own or client material never names a contractor, and the database agrees.
        if (field === 'supplyScope' && value !== 'CONTRACTOR') next.contractorId = ''
        return next
      }),
    )
  }

  const identifierOf = (line: DraftLine) =>
    module === 'WORK' ? line.workItemId : module === 'LABOUR' ? line.labourCategoryId : line.materialId

  const filled = lines.filter((line) => identifierOf(line) !== '')
  const duplicate =
    new Set(filled.map((line) => `${identifierOf(line)}|${line.contractorId}|${line.supplierId}`)).size
    !== filled.length
  const contractorMissing = filled.some(
    (line) => module === 'MATERIAL' && line.supplyScope === 'CONTRACTOR' && !line.contractorId,
  )
  const saving = create.isPending || update.isPending
  const blocked = saving || name.trim().length < 2 || filled.length === 0 || duplicate || contractorMissing

  async function save() {
    setError(null)
    const payload: TemplateLineRequest[] = filled.map((line, index) => ({
      sortOrder: index,
      workItemId: module === 'WORK' ? line.workItemId : undefined,
      labourCategoryId: module === 'LABOUR' ? line.labourCategoryId : undefined,
      materialId: module === 'MATERIAL' ? line.materialId : undefined,
      contractorId:
        module === 'LABOUR'
          ? line.contractorId || undefined
          : module === 'MATERIAL' && line.supplyScope === 'CONTRACTOR'
            ? line.contractorId
            : undefined,
      supplierId: module === 'MATERIAL' ? line.supplierId || undefined : undefined,
      supplyScope: module === 'MATERIAL' ? line.supplyScope : undefined,
      workerCount: line.workerCount === '' ? undefined : Number(line.workerCount),
      workingHours: line.workingHours === '' ? undefined : Number(line.workingHours),
      overtimeHours: line.overtimeHours === '' ? undefined : Number(line.overtimeHours),
      defaultQuantity: line.defaultQuantity === '' ? null : Number(line.defaultQuantity),
      rate: line.rate === '' ? null : Number(line.rate),
    }))

    const body = {
      siteId,
      module,
      name: name.trim(),
      description: description.trim() || undefined,
      active,
      lines: payload,
    }

    try {
      if (template) {
        await update.mutateAsync({ id: template.id, body })
      } else {
        await create.mutateAsync(body)
      }
      onClose()
    } catch (err) {
      setError(err)
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {template ? `Edit ${template.name}` : 'New template'}
        <Typography variant="body2" color="text.secondary">
          {module === 'WORK' && 'The BOQ lines a normal day works on, and how much is planned.'}
          {module === 'LABOUR' && 'The gang expected on site, and how many of each trade.'}
          {module === 'MATERIAL' && 'The deliveries this site regularly orders, and how much.'}
          {' '}These are planned figures — what actually happened is entered on the day.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {Boolean(error) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {describeError(error)}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, mb: 2 }}>
          <TextField
            label="Template name"
            required
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={module === 'LABOUR' ? 'Block A day gang' : 'Standard day'}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Box>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
          label="Available in the template picker"
        />

        <Box sx={{ display: 'grid', gridTemplateColumns: GRID[module], gap: 1, mb: 1 }}>
          {HEADERS[module].map((header, index) => (
            <Typography
              key={`${header}-${index}`}
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {header}
            </Typography>
          ))}
        </Box>

        {lines.map((line) => {
          const workItem = (workItems.data ?? []).find((w) => w.id === line.workItemId)
          return (
            <Box
              key={line.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: GRID[module],
                gap: 1,
                mb: 1,
                alignItems: 'center',
              }}
            >
              {module === 'WORK' && (
                <>
                  <TextField
                    select
                    value={line.workItemId}
                    onChange={(e) => set(line.key, 'workItemId', e.target.value)}
                  >
                    <MenuItem value="">—</MenuItem>
                    {(workItems.data ?? []).map((item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {item.code} — {item.description}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    placeholder={workItem?.uomCode ?? "qty"}
                    value={line.defaultQuantity}
                    onChange={(e) => set(line.key, 'defaultQuantity', e.target.value)}
                  />
                </>
              )}

              {module === 'LABOUR' && (
                <>
                  <TextField
                    select
                    value={line.labourCategoryId}
                    onChange={(e) => set(line.key, 'labourCategoryId', e.target.value)}
                  >
                    <MenuItem value="">—</MenuItem>
                    {(categories.data ?? []).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    value={line.contractorId}
                    onChange={(e) => set(line.key, 'contractorId', e.target.value)}
                  >
                    <MenuItem value="">Own workforce</MenuItem>
                    {(contractors.data ?? []).map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    type="number"
                    placeholder="10"
                    value={line.workerCount}
                    onChange={(e) => set(line.key, 'workerCount', e.target.value)}
                  />
                  <TextField
                    type="number"
                    placeholder="8"
                    value={line.workingHours}
                    onChange={(e) => set(line.key, 'workingHours', e.target.value)}
                  />
                  <TextField
                    type="number"
                    placeholder="0"
                    value={line.overtimeHours}
                    onChange={(e) => set(line.key, 'overtimeHours', e.target.value)}
                  />
                </>
              )}

              {module === 'MATERIAL' && (
                <>
                  <TextField
                    select
                    value={line.materialId}
                    onChange={(e) => set(line.key, 'materialId', e.target.value)}
                  >
                    <MenuItem value="">—</MenuItem>
                    {(materials.data?.content ?? []).map((m) => (
                      <MenuItem key={m.id} value={m.id}>
                        {m.code} — {m.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    value={line.supplierId}
                    onChange={(e) => set(line.key, 'supplierId', e.target.value)}
                  >
                    <MenuItem value="">Not specified</MenuItem>
                    {(suppliers.data ?? []).map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      select
                      fullWidth
                      value={line.supplyScope}
                      onChange={(e) => set(line.key, 'supplyScope', e.target.value)}
                    >
                      <MenuItem value="OWN">Own</MenuItem>
                      <MenuItem value="CONTRACTOR">Contractor</MenuItem>
                      <MenuItem value="CLIENT">Client</MenuItem>
                    </TextField>
                    {line.supplyScope === 'CONTRACTOR' && (
                      <TextField
                        select
                        fullWidth
                        error={!line.contractorId}
                        value={line.contractorId}
                        onChange={(e) => set(line.key, 'contractorId', e.target.value)}
                      >
                        <MenuItem value="">Pick one</MenuItem>
                        {(contractors.data ?? []).map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>
                  <TextField
                    type="number"
                    value={line.defaultQuantity}
                    onChange={(e) => set(line.key, 'defaultQuantity', e.target.value)}
                  />
                  <TextField
                    type="number"
                    value={line.rate}
                    onChange={(e) => set(line.key, 'rate', e.target.value)}
                  />
                </>
              )}

              <Tooltip title="Remove this line">
                <span>
                  <IconButton
                    size="small"
                    disabled={lines.length === 1}
                    onClick={() => setLines((prev) => prev.filter((l) => l.key !== line.key))}
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          )
        })}

        <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((p) => [...p, emptyLine()])}>
          Add line
        </Button>

        {duplicate && (
          <Alert severity="error" sx={{ mt: 2 }}>
            The same entry appears twice. Combine those lines into one.
          </Alert>
        )}
        {contractorMissing && (
          <Alert severity="error" sx={{ mt: 2 }}>
            A contractor-supplied line has to name the contractor.
          </Alert>
        )}
        {filled.length > 0 && !duplicate && !contractorMissing && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {filled.length} line(s). Applying this writes these as the plan for a day
            {module === 'WORK' && ' — completed quantity is left for the engineer to measure.'}
            {module === 'LABOUR' && ' — the headcount that turned up is entered separately.'}
            {module === 'MATERIAL' && ' — received quantity is left for whoever takes delivery.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" disabled={blocked} onClick={() => void save()}>
          {saving ? 'Saving...' : template ? 'Save changes' : 'Create template'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
